import React, {
  ComponentType,
  ForwardedRef,
  forwardRef,
  PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'

import CrossLarge from '@helpscout/hsds-icons/cross-large'
import classNames from 'classnames'

import { useAnimatedRender, useClickOutside } from 'hsds/hooks'
import { useZIndex, ZIndexStackProvider } from 'hsds/hooks/zindex'
import { getClosestFocusableParent, manageTrappedFocus } from 'hsds/utils/focus'
import { getValidProps } from 'hsds/utils/react'
import { CSSPropertiesWithVariables } from 'hsds/utils/types/CSSPropertiesWithVariables'

import { shouldUnsetDimensions, useModalAutoFocus } from './SimpleModal.utils'

import {
  CloseModalButtonUI,
  SimpleModalOverlayUI,
  SimpleModalUI,
} from './SimpleModal.styles'

import type { ExtraRestProps, SimpleModalBaseProps } from './SimpleModal.types'

const noop = () => {}

const DATA_COMPONENTS_ID = {
  OVERLAY: 'simple-modal-overlay',
  MODAL: 'simple-modal',
} as const

function SimpleModal({
  ariaLabelledBy = '',
  blocksGlobalHotkeys = true,
  children,
  className,
  customCloseButton,
  closeOnClickOutside = false,
  'data-cy': dataCy = 'SimpleModal',
  focusModalOnShow = true,
  focusTargetElement = null,
  onModalOpen = noop,
  onClose = noop,
  show = false,
  trapFocus = true,
  width = '360px',
  height = '390px',
  unsetDimensions = false,
  withCloseButton = true,
  zIndex,
  fullscreen = false,
  ...rest
}: SimpleModalBaseProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [shouldRender, onAnimationEnd] = useAnimatedRender(show, overlayRef)

  const [setModalRef, modalRef] = useModalAutoFocus({
    shouldRender,
    focusTargetElement,
    focusModalOnShow,
  })

  // Consumers can force the aria-hidden attribute via the `aria-hidden`
  // (kebab-case, HTMLAttributes) or `ariaHidden` (camelCase, legacy) prop.
  const ariaHiddenOverride =
    (rest as ExtraRestProps).ariaHidden ??
    (rest as Record<string, unknown>)['aria-hidden']

  // The modal is "hidden" whenever `show` is false (or an explicit aria-hidden
  // override says so). We express that hidden state with `inert`, NOT with
  // aria-hidden=true — see below.
  const isHidden =
    ariaHiddenOverride != null ? Boolean(ariaHiddenOverride) : !show

  // Deliberately do NOT flip aria-hidden to true just because the modal is
  // closing. While the modal contains a focused descendant (an autofocused
  // input, or an embedded Wistia/mp4 iframe), aria-hidden=true on an ancestor
  // of that focused element makes the browser log: "Blocked aria-hidden on an
  // element because its descendant retained focus … Consider using the inert
  // attribute instead". Pairing aria-hidden with a slightly-later inert only
  // *narrows* that window — a fast open/close still loses the race. So we remove
  // the precondition entirely: only reflect an explicit aria-hidden override
  // here, and hide the closing modal via `inert` (below), which is exactly the
  // replacement the spec + the browser message recommend.
  const ariaHidden =
    ariaHiddenOverride != null ? Boolean(ariaHiddenOverride) : false

  // Hide the modal from assistive tech and pull focus out of it via `inert`: an
  // inert subtree is removed from the a11y tree, can't hold or receive focus,
  // and setting it runs the browser's focus-fixup to blur any focused
  // descendant — with no programmatic focus() (which would read as a window
  // refocus and trigger refetchOnWindowFocus for active queries). useLayoutEffect
  // lands it before paint. React 17 doesn't render `inert`, so set it on the DOM
  // node. Mirrors Accordion.Body. https://github.com/facebook/react/issues/17157
  useLayoutEffect(() => {
    if (modalRef.current == null) return

    if (isHidden) {
      modalRef.current.setAttribute('inert', '')
    } else {
      modalRef.current.removeAttribute('inert')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden, shouldRender])

  useEffect(() => {
    if (shouldRender) {
      setTimeout(() => {
        onModalOpen(modalRef)
      }, 300)
    } else {
      if (overlayRef.current) {
        const focusableParent = getClosestFocusableParent(
          overlayRef.current
        ) as HTMLElement | null
        focusableParent?.focus()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRender, focusModalOnShow])

  useClickOutside(getClickOutsideRef(), (e: React.MouseEvent | MouseEvent) => {
    const { target } = e

    if (!(target instanceof Element)) return

    if (
      target.getAttribute('data-component-id') === DATA_COMPONENTS_ID.MODAL &&
      target !== modalRef.current
    ) {
      return
    }

    if (
      target.getAttribute('data-component-id') === DATA_COMPONENTS_ID.OVERLAY &&
      target.firstElementChild !== modalRef.current
    ) {
      return
    }

    const targetParentModal = target.closest(
      `[data-component-id="${DATA_COMPONENTS_ID.MODAL}"]`
    )

    if (targetParentModal && targetParentModal !== modalRef.current) {
      return
    }

    // if the target is within a data-floating-ui-portal or editor-portal, don't close the modal
    // data-floating-ui-portal can be a droplist, popover, tooltip, simple-modal
    // editor-portal is used by the editor for contextual toolbars
    if (
      ((e.target as HTMLElement)?.closest('[data-floating-ui-portal]') ||
        (e.target as HTMLElement)?.closest('.editor-portal')) &&
      !(e.target as HTMLElement)?.closest('.SimpleModal__Overlay')
    ) {
      return
    }
    onClose(e)
  })

  function getClickOutsideRef() {
    if (closeOnClickOutside === 'modal') {
      return modalRef
    }
    if (closeOnClickOutside === 'overlay') {
      return overlayRef
    }

    return null
  }

  function handleOverlayKeyDown(e: React.KeyboardEvent) {
    e.stopPropagation()

    if (shouldRender && e.key === 'Escape') {
      onClose(e)
    } else if (e.key === 'Tab' && trapFocus && modalRef.current) {
      manageTrappedFocus(modalRef.current, e)
    }
  }

  function renderCloseButton() {
    if (withCloseButton) {
      if (customCloseButton && React.isValidElement(customCloseButton)) {
        const clickHandler = customCloseButton.props.onClick || onClose

        return React.cloneElement(customCloseButton, {
          onClick: clickHandler,
        })
      }

      return (
        <CloseModalButtonUI
          aria-label="close modal button"
          className="SimpleModal__CloseButton"
          onClick={(e: React.MouseEvent | MouseEvent) => {
            e.stopPropagation()
            onClose(e)
          }}
          color="white"
          icon={CrossLarge}
          size="md"
        />
      )
    }

    return null
  }

  const clearDimensions = shouldUnsetDimensions(
    unsetDimensions,
    React.isValidElement(children) &&
      typeof (children.type as ComponentType).name === 'string'
      ? (children.type as ComponentType).name
      : undefined
  )

  const shouldSetWidth = !clearDimensions && width !== '360px'
  const shouldSetHeight = !clearDimensions && height !== '390px'

  // Use useZIndex to resolve the z-index, accounting for parent contexts
  const resolvedZIndex = useZIndex({ zIndex }, 'modal')

  return shouldRender ? (
    <ZIndexStackProvider context="modal" zIndex={resolvedZIndex}>
      <SimpleModalOverlay
        className={classNames(
          'SimpleModal__Overlay',
          show && 'element-in',
          fullscreen && 'is-fullscreen',
          className
        )}
        data-component-id={DATA_COMPONENTS_ID.OVERLAY}
        data-testid={DATA_COMPONENTS_ID.OVERLAY}
        onAnimationEnd={e => {
          e.stopPropagation()
          onAnimationEnd(e)
        }}
        onKeyDown={handleOverlayKeyDown}
        ref={overlayRef}
        zIndex={resolvedZIndex}
      >
        <SimpleModalUI
          aria-hidden={ariaHidden}
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          className={classNames(
            'SimpleModal',
            clearDimensions && 'cleared-dimensions'
          )}
          data-cy={dataCy}
          data-component-id={DATA_COMPONENTS_ID.MODAL}
          data-testid={DATA_COMPONENTS_ID.MODAL}
          data-blocks-global-hotkeys={blocksGlobalHotkeys}
          id={DATA_COMPONENTS_ID.MODAL}
          role="dialog"
          ref={setModalRef}
          style={
            {
              ...(shouldSetHeight && { '--hsds-simpleModal-height': height }),
              ...(shouldSetWidth && { '--hsds-simpleModal-width': width }),
            } as React.CSSProperties
          }
          tabIndex={0}
          {...getValidProps(rest)}
        >
          {renderCloseButton()}
          {children}
        </SimpleModalUI>
      </SimpleModalOverlay>
    </ZIndexStackProvider>
  ) : null
}

const SimpleModalOverlay = forwardRef(function SimpleModalOverlay(
  {
    children,
    zIndex,
    ...props
  }: PropsWithChildren<
    Omit<SimpleModalBaseProps, 'zIndex'> & {
      zIndex: number | string | undefined
    }
  >,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <SimpleModalOverlayUI
      {...getValidProps(props)}
      ref={ref}
      style={
        {
          ...(zIndex ? { '--hsds-simpleModal-overlay-z-index': zIndex } : {}),
        } as CSSPropertiesWithVariables
      }
    >
      {children}
    </SimpleModalOverlayUI>
  )
})

export default SimpleModal
